// ---------------------------------------------------------------------------
// Global error boundary — catches render errors in the component tree
// ---------------------------------------------------------------------------

import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error details for observability
    console.error('[ErrorBoundary] Uncaught render error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-surface-sunken p-6"
          role="alert"
        >
          <div className="max-w-md w-full text-center">
            {/* Error icon */}
            <div className="mx-auto mb-6 w-16 h-16 rounded-sm bg-danger-tint flex items-center justify-center">
              <svg
                className="h-8 w-8 text-danger"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1 className="text-xl font-bold text-ink mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-ink-muted mb-6">
              An unexpected error occurred. You can try reloading the page or
              going back.
            </p>

            {/* Error details — only visible in development builds */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 text-start">
                <summary className="text-xs text-ink-muted cursor-pointer hover:text-ink-muted">
                  Error details
                </summary>
                <pre className="mt-2 p-3 bg-surface-sunken rounded-sm text-xs text-danger overflow-auto max-h-40">
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}

            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={this.handleReset}
                className="px-4 py-2 text-sm font-medium text-ink bg-surface-sunken hover:bg-surface-sunken rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-muted focus-visible:ring-offset-2"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
